/**
 * Centre pane of the adjudication flow — per `feature-spec.md` §3.5.2.
 *
 * For each dimension renders a collapsible card with:
 *   - Header: status dot (green = both annotators picked identical sets,
 *     amber = mismatch) + dimension name + chevron. Click → toggle.
 *   - Body (when expanded): two-column grid of annotator selections.
 *     Labels in BOTH sets render as `success` (green/shared); labels in
 *     ONLY ONE set render as `attention` (amber/unique). Below the
 *     labels: each annotator's `confidenceNote` (italic, muted "No note"
 *     if absent).
 *
 * Annotator-name gap (see plan): no user-lookup hook exists, so columns
 * are headed `"Annotator 1"` / `"Annotator 2"` with the annotator's uuid
 * surfaced via the `title` attribute for debugging.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { useLabelNames } from '../../../shared/useLabelNames';
import type { Annotation, UserSummary } from '../../types';
import type { TaxonomyDimension } from '../../../types/taxonomy';

interface Props {
  dimensions: TaxonomyDimension[];
  firstAnnotation: Annotation | null;
  secondAnnotation: Annotation | null;
  expandedDimensions: Record<string, boolean>;
  onToggleExpanded: (dimId: string) => void;
  /**
   * Optional `userId → UserSummary` map used to render real annotator
   * names in the column headers (B15-FE). When absent (lookup still
   * loading, fetch failed, or id missing from the response) the column
   * falls back to the `"Annotator 1"` / `"Annotator 2"` placeholders.
   */
  namesByAnnotatorId?: Record<string, UserSummary>;
}

const setsEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((x) => bs.has(x));
};

interface AnnotatorColumnProps {
  role: 'first' | 'second';
  roleLabel: string;
  labels: string[];
  sharedLabelIds: Set<string>;
  labelNameById: Record<string, string>;
  confidenceNote: string | null;
}

const AnnotatorColumn: React.FC<AnnotatorColumnProps> = ({
  role,
  roleLabel,
  labels,
  sharedLabelIds,
  labelNameById,
  confidenceNote,
}) => (
  <div
    className="flex flex-col gap-2"
    data-testid={`annotator-column-${role}`}
  >
    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {roleLabel}
    </div>
    {labels.length === 0 ? (
      <Badge variant="secondary" data-testid={`annotator-${role}-none-apply`}>
        None apply
      </Badge>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {labels.map((labelId) => {
          const shared = sharedLabelIds.has(labelId);
          return (
            <Badge
              key={labelId}
              variant={shared ? 'success' : 'attention'}
              data-testid={`annotator-${role}-label-${labelId}`}
              data-shared={shared}
            >
              {labelNameById[labelId] ?? labelId}
            </Badge>
          );
        })}
      </div>
    )}
    <p
      className="text-[12px] italic text-muted-foreground"
      data-testid={`annotator-${role}-confidence-note`}
    >
      {confidenceNote && confidenceNote.trim().length > 0
        ? confidenceNote
        : 'No note'}
    </p>
  </div>
);

const ComparisonPane: React.FC<Props> = ({
  dimensions,
  firstAnnotation,
  secondAnnotation,
  expandedDimensions,
  onToggleExpanded,
  namesByAnnotatorId,
}) => {
  // dim id → label id → display name; collapsed flat across categories.
  const labelNamesByDim = useLabelNames(dimensions);

  // B15-FE: resolve the display label per column. Fall back to placeholder
  // when (a) the names map isn't available (hook loading / failed), (b) the
  // annotation itself is null, (c) the BE didn't return a record for that
  // id (cross-org / deleted), or (d) the record's `name` is null / blank.
  const labelForAnnotator = (
    annotatorId: string | null | undefined,
    fallback: string,
  ): string => {
    if (!annotatorId) return fallback;
    const name = namesByAnnotatorId?.[annotatorId]?.name;
    return name && name.trim().length > 0 ? name : fallback;
  };
  const firstLabel = labelForAnnotator(
    firstAnnotation?.annotatorId ?? null,
    'Annotator 1',
  );
  const secondLabel = labelForAnnotator(
    secondAnnotation?.annotatorId ?? null,
    'Annotator 2',
  );

  return (
    <div className="space-y-3" data-testid="comparison-pane">
      {dimensions.map((dim) => {
        const first = firstAnnotation?.labels?.[dim.id] ?? [];
        const second = secondAnnotation?.labels?.[dim.id] ?? [];
        const agreed = setsEqual(first, second);
        const sharedLabelIds = new Set(
          first.filter((labelId) => second.includes(labelId)),
        );
        const expanded = expandedDimensions[dim.id] ?? !agreed;

        return (
          <div
            key={dim.id}
            className="rounded-lg border border-border bg-background"
            data-testid={`comparison-dimension-${dim.id}`}
          >
            <button
              type="button"
              onClick={() => onToggleExpanded(dim.id)}
              className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/40"
              data-testid={`comparison-dimension-header-${dim.id}`}
              aria-expanded={expanded}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  agreed
                    ? 'bg-[var(--go-success)]'
                    : 'bg-[var(--go-bittersweet)]'
                }`}
                data-testid={`comparison-status-dot-${dim.id}`}
                data-agreed={agreed}
                aria-hidden
              />
              <span className="flex-1 text-[13px] font-semibold text-foreground">
                {dim.name}
              </span>
              <span
                className="text-[11px] uppercase tracking-wider text-muted-foreground"
                data-testid={`comparison-status-label-${dim.id}`}
              >
                {agreed ? 'Agreed' : 'Disputed'}
              </span>
              <span
                className="text-[12px] text-muted-foreground"
                aria-hidden
              >
                {expanded ? '▾' : '▸'}
              </span>
            </button>

            {expanded && (
              <div
                className="grid grid-cols-1 gap-4 border-t border-border px-4 py-3 md:grid-cols-2"
                data-testid={`comparison-dimension-body-${dim.id}`}
              >
                <AnnotatorColumn
                  role="first"
                  roleLabel={firstLabel}
                  labels={first}
                  sharedLabelIds={sharedLabelIds}
                  labelNameById={labelNamesByDim[dim.id] ?? {}}
                  confidenceNote={firstAnnotation?.confidenceNote ?? null}
                />
                <AnnotatorColumn
                  role="second"
                  roleLabel={secondLabel}
                  labels={second}
                  sharedLabelIds={sharedLabelIds}
                  labelNameById={labelNamesByDim[dim.id] ?? {}}
                  confidenceNote={secondAnnotation?.confidenceNote ?? null}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ComparisonPane;
